import React from "react";

import { PropTypes } from "prop-types";
import { useSelector } from "react-redux";

import AgGrid from "components/AgGrid/AgGrid";
import { CompositeFilter } from "components/AgGrid/filters/compositeFilter";
import { CollapseTableWrapper } from "components/AgGrid/TableWrapper";
import { Button, SecondaryButton } from "components/Form";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import { ACTION_COLUMN_NAME } from "constants/aggrid";

import { getDefaultPropertyId } from "lib/properties";

import { getCurrentSaleyardId, getProperties } from "selectors";

import { actionsColumnRenderer } from "./Actions";

const defaultColDef = {
  sortable: true,
  resizable: true,
};

const components = {
  [CompositeFilter.componentName]: CompositeFilter,
};

export const BusinessTableConfiguration = {
  SG8XML: 0,
  AuctionsPlusCSV: 1,
};

const BusinessTableColumns = {
  [BusinessTableConfiguration.SG8XML]: [
    {
      headerName: "Business Name",
      field: "name",
    },
    {
      headerName: "Agent Code",
      field: "shortCode",
    },
    {
      headerName: "SY Code",
      field: "shortCodeSaleyard",
    },

    {
      headerName: "Default PIC",
      valueGetter: ({ data, context }) => {
        const propertyId = getDefaultPropertyId(data, context.saleyardId);
        return context.propertyByIdLookup[propertyId]?.PIC || null;
      },
    },
    {
      headerName: "PICS",
      valueGetter: ({ data }) => data.properties.map(p => p.PIC),
    },
    {
      headerName: "Branch",
      field: "branchName",
    },
  ],
  [BusinessTableConfiguration.AuctionsPlusCSV]: [
    {
      headerName: "Business Name",
      field: "name",
    },
    {
      headerName: "AuctionsPlus Short Code",
      field: "shortCodeAuctionsPlus",
    },
    {
      headerName: "Town",
      field: "address.locality",
    },

    {
      headerName: "State",
      field: "address.state",
    },
  ],
};

const checkIsOverrideFunctions = {
  [BusinessTableConfiguration.SG8XML]: data => data.shortCodeSaleyard,
  [BusinessTableConfiguration.AuctionsPlusCSV]: data =>
    data.shortCodeAuctionsPlus,
};

const confirmOverrideMessages = {
  [BusinessTableConfiguration.SG8XML]: (business, data) =>
    `Do you want to override the current saleyard code "${business.shortCodeSaleyard}" with "${data.businessShortCode}"`,
  [BusinessTableConfiguration.AuctionsPlusCSV]: (business, data) =>
    `Do you want to override the current AuctionsPlus Short Code "${business.shortCodeAuctionsPlus}" for "${business.name}" with "${data.auctionsPlusId}"`,
};

const BusinessTable = ({
  data,
  importData,
  mapBusiness,
  tableConfiguration,
  paginationPageSize = 400,
}) => {
  const [selectedBusiness, setSelectedBusiness] = React.useState(null);
  const saleyardId = useSelector(getCurrentSaleyardId);
  const propertyByIdLookup = useSelector(getProperties);

  const columnDefs = React.useMemo(() => {
    return [
      ...BusinessTableColumns[
        tableConfiguration || BusinessTableConfiguration.SG8XML
      ],
      {
        headerName: ACTION_COLUMN_NAME,
        cellRenderer: actionsColumnRenderer,
        pinned: "right",
        cellRendererParams: {
          checkIsOverride: checkIsOverrideFunctions[tableConfiguration],
          setSelectedBusiness,
          mapBusiness,
        },
      },
    ];
  }, [mapBusiness, tableConfiguration]);

  const clearConfirmingBusiness = () => setSelectedBusiness(false);
  return (
    <>
      <CollapseTableWrapper>
        <AgGrid
          domLayout="autoHeight"
          rowData={data}
          suppressColumnVirtualisation
          suppressScrollOnNewData
          pagination
          paginationPageSize={paginationPageSize}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowBuffer={10}
          components={components}
          context={{ saleyardId, propertyByIdLookup }}
        />
      </CollapseTableWrapper>

      {selectedBusiness && (
        <Dialog open onClose={clearConfirmingBusiness} maxWidth="sm" fullWidth>
          <DialogTitle id="form-dialog-title" onClose={clearConfirmingBusiness}>
            Are you sure
          </DialogTitle>
          <DialogContent dividers>
            <div>
              {confirmOverrideMessages[tableConfiguration](
                selectedBusiness,
                importData,
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <SecondaryButton onClick={clearConfirmingBusiness}>
              Cancel
            </SecondaryButton>
            <Button onClick={() => mapBusiness(selectedBusiness, false)}>
              No
            </Button>
            <Button onClick={() => mapBusiness(selectedBusiness, true)}>
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

BusinessTable.propTypes = {
  mapBusiness: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
};

export default BusinessTable;
